import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { History, FileText, UploadCloud, CheckCircle2, RotateCcw, Loader2, AlertCircle, X, Eye, Download, MessageSquare } from "lucide-react";
import { listSubmissions, createSubmission, requestChanges, approveVersion, generateSubmissionId } from "../services/submissions";
import { createChangeRequest, listChangeRequests } from "../services/changeRequests";
import { uploadFileResumable, downloadCaseFile } from "../services/storage";
import { CAD_ACCEPT, isCadFile } from "../lib/cad";
import { StlViewer } from "./StlViewer";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TranslatableText } from "./TranslatableText";

export const SubmissionVersions = ({ caseItem, isOwner, isSelectedDesigner, user, profile, onChanged }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] === "es" ? "es" : "en";
  const [versions, setVersions] = useState([]);
  const [changeReqs, setChangeReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");
  const [intent, setIntent] = useState("review");
  const [crOpen, setCrOpen] = useState(false);
  const [crNote, setCrNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [upload, setUpload] = useState({ status: "idle", pct: 0 }); // idle|uploading|processing|done|error
  const [viewerFile, setViewerFile] = useState(null); // { url, name } for the "View STL" dialog
  const [dlBusy, setDlBusy] = useState("");

  // Secure deliverable download: goes through the backend, which verifies the caller
  // is the case owner / selected designer / admin and returns a short-lived signed URL.
  const handleDownload = async (f) => {
    setDlBusy(f.url);
    try {
      await downloadCaseFile(caseId, f.url, f.name);
    } catch (e) {
      const s = e?.response?.status;
      toast.error(s === 403 ? t("files.notAuthorized") : t("files.downloadFailed"));
    } finally {
      setDlBusy("");
    }
  };

  const canView = isOwner || isSelectedDesigner;
  const status = caseItem.status;
  const caseId = caseItem.id;

  const fmt = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleString(lang === "es" ? "es-ES" : "en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return "";
    }
  };

  const load = useCallback(async () => {
    try {
      const [subs, crs] = await Promise.all([listSubmissions(caseId), listChangeRequests(caseId)]);
      setVersions(subs);
      setChangeReqs(crs);
    } catch {
      /* soft-fail */
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (canView) load();
    else setLoading(false);
  }, [canView, load, status]);

  const hasCad = files.some(isCadFile);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length) setFiles((prev) => [...prev, ...incoming]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submitVersion = async () => {
    if (!hasCad) {
      toast.error(t("submission.needFile"));
      return;
    }
    setBusy(true);
    try {
      const submissionId = generateSubmissionId();
      let uploaded = [];
      setUpload({ status: "uploading", pct: 0 });
      try {
        for (let i = 0; i < files.length; i++) {
          const u = await uploadFileResumable(files[i], `submissions/${caseId}/${submissionId}`, (pct) => {
            const overall = Math.round(((i + pct / 100) / files.length) * 100);
            setUpload({ status: overall >= 100 ? "processing" : "uploading", pct: overall });
          });
          uploaded.push({ url: u.url, name: u.name });
        }
        setUpload({ status: "done", pct: 100 });
      } catch {
        setUpload({ status: "error", pct: 0 });
        toast.error(t("upload.error"));
        setBusy(false);
        return;
      }
      const v = await createSubmission(caseId, user.uid, submissionId, { files: uploaded, notes, intent });
      toast.success(`${t("submission.submitted")} · ${t("submission.version")} ${v}`);
      setFiles([]);
      setNotes("");
      setIntent("review");
      setUpload({ status: "idle", pct: 0 });
      await load();
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.message || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const doRequestChanges = async () => {
    const note = crNote.trim();
    if (!note) {
      toast.error(t("submission.changeNotePlaceholder"));
      return;
    }
    setBusy(true);
    try {
      const latest = versions[versions.length - 1];
      await createChangeRequest(caseId, latest?.version ?? 0, {
        authorId: user.uid,
        authorName: profile?.name || user.email,
        note,
      });
      await requestChanges(caseId);
      toast.success(t("submission.changesRequested"));
      setCrNote("");
      setCrOpen(false);
      await load();
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async (version) => {
    setBusy(true);
    try {
      await approveVersion(caseId, version, { uid: user?.uid, name: profile?.name || user?.email || null });
      setApproveOpen(false);
      toast.success(`${t("submission.approved")} · ${t("submission.version")} ${version}`);
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!canView) return null;

  const latest = versions[versions.length - 1];

  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid="submission-versions">
      <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
        <History className="h-5 w-5" /> {t("submission.versionHistory")}
      </h3>

      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : versions.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="no-versions">{t("submission.noVersions")}</p>
      ) : (
        <div className="space-y-3">
          {versions.map((v) => {
            const approved = caseItem.approvedVersion === v.version;
            return (
              <div key={v.id} className="rounded-md border border-border p-4" data-testid={`submission-version-${v.version}`}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="rounded-full font-mono">
                      {t("submission.version")} {v.version}
                    </Badge>
                    {v.intent && (
                      <Badge variant="outline" className={`rounded-full text-[10px] ${v.intent === "final" ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-600"}`} data-testid={`version-intent-${v.version}`}>
                        {v.intent === "final" ? t("submission.finalBadge") : t("submission.reviewBadge")}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground" data-testid={`submission-time-${v.version}`}>{fmt(v._ts)}</span>
                  </div>
                  {approved && (
                    <Badge className="rounded-full bg-emerald-600 text-white hover:bg-emerald-600" data-testid={`approved-badge-${v.version}`}>
                      <CheckCircle2 className="mr-1 h-3 w-3" /> {t("submission.approved")}
                    </Badge>
                  )}
                </div>
                {v.notes && (
                  <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50/70 p-3.5" data-testid={`submission-notes-${v.version}`}>
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-violet-700">
                      <MessageSquare className="h-3.5 w-3.5" /> {t("submission.designerNote")}
                    </p>
                    <p className="text-[15px] leading-relaxed text-foreground">{v.notes}</p>
                  </div>
                )}
                {v.files?.length > 0 && (
                  <ul className="space-y-1.5">
                    {v.files.map((f, i) => {
                      const cad = isCadFile(f.name);
                      return (
                        <li key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
                          <span className="flex items-center gap-2 truncate text-sm font-medium" data-testid={`submission-file-${v.version}-${i}`}>
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" /> {f.name}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {cad && (
                              <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => setViewerFile(f)} data-testid={`view-stl-${v.version}-${i}`}>
                                <Eye className="mr-1 h-3.5 w-3.5" /> {t("submission.viewStl")}
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8 rounded-full text-brand hover:bg-brand/10" disabled={dlBusy === f.url} onClick={() => handleDownload(f)} data-testid={`download-stl-${v.version}-${i}`}>
                              {dlBusy === f.url ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} {t("submission.downloadStl")}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {changeReqs.filter((cr) => cr.version === v.version).map((cr) => (
                  <div key={cr.id} className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`change-request-${cr.id}`}>
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                        <RotateCcw className="h-3 w-3" /> {t("submission.changeRequestTitle")}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{cr.authorName} · {fmt(cr._ts)}</span>
                    </div>
                    <TranslatableText text={cr.note} testid={`change-request-note-${cr.version}`} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Review action card: shown AFTER the version content so the lab reviews the
          designer's note and files first, then decides. */}
      {isOwner && status === "Submitted" && latest && (
        <div className="mt-5 rounded-xl border-2 border-violet-300 bg-violet-50/70 p-5" data-testid="review-action-card">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <Badge className="rounded-full bg-violet-600 text-white hover:bg-violet-600">{t("submission.newVersionArrived")}</Badge>
            <span className="font-mono text-xs text-muted-foreground">
              {t("submission.version")} {latest.version} · {latest.intent === "final" ? t("submission.finalBadge") : t("submission.reviewBadge")}
            </span>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">{t("submission.reviewPrompt")}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button size="lg" className="flex-1 rounded-full bg-emerald-600 text-base font-semibold text-white hover:bg-emerald-700" onClick={() => setApproveOpen(true)} disabled={busy} data-testid="approve-version-cta">
              <CheckCircle2 className="mr-1.5 h-5 w-5" /> {t("submission.approveVersion")}
            </Button>
            <Button size="lg" variant="outline" className="flex-1 rounded-full border-amber-300 text-base font-semibold text-amber-700 hover:bg-amber-50" onClick={() => setCrOpen(true)} disabled={busy} data-testid="request-changes-button">
              <RotateCcw className="mr-1.5 h-5 w-5" /> {t("submission.requestChanges")}
            </Button>
          </div>
          {crOpen && (
            <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
              <Textarea rows={3} value={crNote} onChange={(e) => setCrNote(e.target.value)} placeholder={t("submission.changeNotePlaceholder")} data-testid="change-request-note" />
              <div className="flex gap-2">
                <Button className="flex-1 rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={doRequestChanges} disabled={busy || !crNote.trim()} data-testid="submit-change-request">
                  <RotateCcw className="mr-1.5 h-4 w-4" /> {t("submission.sendChangeRequest")}
                </Button>
                <Button variant="ghost" className="rounded-full" onClick={() => { setCrOpen(false); setCrNote(""); }} disabled={busy}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status hints */}
      {status === "Submitted" && (
        <p className="mt-3 text-xs text-violet-600" data-testid="awaiting-review">{t("submission.awaitingReview")}</p>
      )}

      {/* Designer: submit a (new) version while In Progress */}
      {isSelectedDesigner && status === "In Progress" && (
        <div className="mt-4 space-y-2 border-t border-border pt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {versions.length > 0 ? t("submission.submitNewVersion") : t("submission.submitWork")}
          </p>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-sm text-muted-foreground hover:border-slate-300">
            <UploadCloud className="h-4 w-4" /> {files.length ? t("cad.addMore") : t("submission.uploadFiles")}
            <input type="file" accept={`${CAD_ACCEPT},image/*`} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="submission-files-input" />
          </label>
          {files.length > 0 && (
            <ul className="space-y-1" data-testid="submission-files-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                  <span className={`truncate font-mono text-xs ${isCadFile(f) ? "text-foreground" : "text-muted-foreground"}`}>{f.name}{!isCadFile(f) && " (preview)"}</span>
                  <button type="button" onClick={() => removeFile(i)} data-testid={`submission-file-remove-${i}`} aria-label={t("cad.remove")}>
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!hasCad && files.length > 0 && (
            <p className="flex items-center gap-1 text-xs text-amber-600" data-testid="need-cad-warning">
              <AlertCircle className="h-3.5 w-3.5" /> {t("submission.needFile")}
            </p>
          )}
          <div className="rounded-md border border-border p-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("submission.intentTitle")}</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="intent-review">
                <input type="radio" name="intent" checked={intent === "review"} onChange={() => setIntent("review")} /> {t("submission.intentReview")}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="intent-final">
                <input type="radio" name="intent" checked={intent === "final"} onChange={() => setIntent("final")} /> {t("submission.intentFinal")}
              </label>
            </div>
          </div>
          {upload.status !== "idle" && (
            <div className="rounded-md border border-border bg-secondary/20 p-3" data-testid="submission-upload-progress">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-medium">
                  <UploadCloud className="h-3.5 w-3.5" /> {t("upload.title")}
                </span>
                {upload.status === "uploading" && (
                  <span className="flex items-center gap-1 font-mono text-xs text-violet-600" data-testid="submission-upload-percent">
                    <Loader2 className="h-3 w-3 animate-spin" /> {upload.pct}%
                  </span>
                )}
                {upload.status === "processing" && (
                  <span className="flex items-center gap-1 text-xs text-violet-600"><Loader2 className="h-3 w-3 animate-spin" /> {t("upload.processing")}</span>
                )}
                {upload.status === "done" && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid="submission-upload-done"><CheckCircle2 className="h-3.5 w-3.5" /> {t("upload.done")}</span>
                )}
                {upload.status === "error" && (
                  <span className="flex items-center gap-1 text-xs text-red-600" data-testid="submission-upload-error"><AlertCircle className="h-3.5 w-3.5" /> {t("upload.error")}</span>
                )}
              </div>
              {upload.status !== "error" && (
                <Progress value={upload.status === "processing" ? 100 : upload.pct} className="h-1.5" data-testid="submission-upload-bar" />
              )}
              {upload.status === "error" && (
                <Button size="sm" className="mt-2 w-full rounded-full bg-violet-600 text-white hover:bg-violet-700" onClick={submitVersion} disabled={busy} data-testid="submission-upload-retry">
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {t("upload.retry")}
                </Button>
              )}
            </div>
          )}
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("submission.notesPlaceholder")} data-testid="submission-notes" />
          <Button className="w-full rounded-full bg-violet-600 text-base font-semibold text-white hover:bg-violet-700" onClick={submitVersion} disabled={busy || !hasCad} data-testid="submit-version-button">
            <UploadCloud className="mr-1.5 h-4 w-4" /> {busy ? t("submission.uploading") : versions.length > 0 ? t("submission.submitNewVersion") : t("submission.submitWork")}
          </Button>
        </div>
      )}

      <Dialog open={!!viewerFile} onOpenChange={(v) => !v && setViewerFile(null)}>
        <DialogContent className="max-w-3xl" data-testid="submission-stl-dialog">
          <DialogHeader>
            <DialogTitle className="truncate font-mono text-sm">{viewerFile?.name}</DialogTitle>
          </DialogHeader>
          {viewerFile && <StlViewer url={viewerFile.url} name={viewerFile.name} />}
        </DialogContent>
      </Dialog>

      <Dialog open={approveOpen} onOpenChange={(v) => !busy && setApproveOpen(v)}>
        <DialogContent className="max-w-md" data-testid="approve-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("submission.approveConfirmTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-muted-foreground" data-testid="approve-confirm-body">
            {t("submission.approveConfirmBody")}
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row-reverse">
            <Button
              className="flex-1 rounded-full bg-emerald-600 text-base font-semibold text-white hover:bg-emerald-700"
              onClick={() => latest && doApprove(latest.version)}
              disabled={busy}
              data-testid="approve-confirm-cta"
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-5 w-5" />}
              {t("submission.approveConfirmCta")}
            </Button>
            <Button
              variant="outline"
              className="flex-1 rounded-full text-base font-semibold"
              onClick={() => setApproveOpen(false)}
              disabled={busy}
              data-testid="approve-confirm-cancel"
            >
              {t("common.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
